/**
 * @fileoverview Offline banner component.
 * @summary Banner displayed when the user is offline.
 *
 * @package KaraoNow
 * @module apps/front/src/components/OfflineBanner
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 * @license EULA
 */
import React, { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faWifi } from '@fortawesome/free-solid-svg-icons'

/**
 * @summary Offline banner component.
 * @description Shows a banner when the user loses internet connection.
 * @returns {JSX.Element | null} Offline banner or null if online.
 * @author Pierre-Yves Léglise <contact@axialdata.net>
 */
export function OfflineBanner(): React.ReactElement | null {
  const { t } = useTranslation()
  const [isOffline, setIsOffline] = useState(!navigator.onLine)

  useEffect(() => {
    const handleOnline = () => setIsOffline(false)
    const handleOffline = () => setIsOffline(true)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  if (!isOffline) {
    return null
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-yellow-500 text-yellow-900 px-4 py-2 flex items-center justify-center gap-2 z-50">
      <FontAwesomeIcon icon={faWifi} className="w-4 h-4" />
      <span className="font-medium">{t('pwa.offline')}</span>
      <span className="text-sm opacity-80">- {t('pwa.offlineMessage')}</span>
    </div>
  )
}

export default OfflineBanner

